import { useEffect, useState } from "react";
import {
  Users, UserPlus, Activity, DollarSign, TrendingUp, CreditCard, Wallet,
  AlertTriangle, Building2, FileText, Bot, CheckCircle2, HeartPulse, Loader2,
} from "lucide-react";
import api from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const num = (n) => Number(n || 0).toLocaleString();

function Metric({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${accent ? "fv-gold-gradient" : "bg-[#D4AF37]/10 border border-[#D4AF37]/20"}`}>
        <Icon className={`w-5 h-5 ${accent ? "text-[#0A0A0B]" : "text-[#D4AF37]"}`} />
      </div>
      <div className="font-heading text-3xl text-white tabular-nums">{value}</div>
      <div className="text-[#71717A] text-sm mt-1">{label}</div>
      {sub && <div className="text-[11px] text-[#52525B] mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <h3 className="text-xs uppercase tracking-wider text-[#71717A] mb-3">{title}</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{children}</div>
    </div>
  );
}

export default function AdminExecutive() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/admin/executive").then((r) => setD(r.data)).catch(() => setErr(true)); }, []);

  if (err) return <p className="text-sm text-[#71717A]" data-testid="admin-exec-denied">You don't have access to the executive dashboard.</p>;
  if (!d) return <div className="flex justify-center py-20 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const h = d.health;
  const statusColor = h.status === "operational" ? "text-emerald-400 bg-emerald-500/15"
    : h.status === "degraded" ? "text-amber-400 bg-amber-500/15" : "text-red-400 bg-red-500/15";

  return (
    <div data-testid="admin-executive">
      {/* Health hero */}
      <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6 mb-6 flex flex-col sm:flex-row items-center gap-6" data-testid="admin-health-hero">
        <div className="relative w-24 h-24 shrink-0">
          <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
            <circle cx="50" cy="50" r="42" fill="none" stroke="#D4AF37" strokeWidth="8" strokeLinecap="round"
              strokeDasharray={264} strokeDashoffset={264 - (264 * h.score) / 100} style={{ transition: "stroke-dashoffset 1s ease" }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-heading text-2xl text-white tabular-nums">{h.score}</span>
            <span className="text-[9px] uppercase tracking-wider text-[#71717A]">Health</span>
          </div>
        </div>
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <div className="flex items-center gap-2 justify-center sm:justify-start mb-1">
            <HeartPulse className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-sm uppercase tracking-wider text-[#D4AF37]">Platform Health</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${statusColor}`} data-testid="admin-platform-status">{h.status}</span>
          </div>
          <p className="text-sm text-[#A1A1AA]">
            Database {h.db_ok ? "connected" : "unreachable"}{h.db_latency_ms != null ? ` · ${h.db_latency_ms}ms` : ""} · uptime {Math.floor(h.uptime_seconds / 60)}m
          </p>
          <p className="text-[11px] text-[#52525B] mt-1">Live snapshot · {new Date(d.generated_at).toLocaleString()}</p>
        </div>
      </div>

      <Section title="Users">
        <Metric icon={Users} label="Total users" value={num(d.users.total)} accent testid="exec-total-users" />
        <Metric icon={Activity} label="Active (MAU)" value={num(d.users.active)} sub="Active in last 30 days" testid="exec-active-users" />
        <Metric icon={UserPlus} label="New today" value={num(d.users.new_today)} testid="exec-new-today" />
        <Metric icon={UserPlus} label="New (30 days)" value={num(d.users.new_30d)} testid="exec-new-30d" />
      </Section>

      <Section title="Revenue">
        <Metric icon={DollarSign} label="MRR" value={money(d.revenue.mrr)} accent testid="exec-mrr" />
        <Metric icon={TrendingUp} label="ARR" value={money(d.revenue.arr)} testid="exec-arr" />
        <Metric icon={Wallet} label="Total revenue" value={money(d.revenue.total)} sub="All paid transactions" testid="exec-revenue" />
        <Metric icon={DollarSign} label="ARPU" value={money(d.revenue.arpu)} sub="Per paying user / mo" testid="exec-arpu" />
      </Section>

      <Section title="Subscriptions">
        <Metric icon={CreditCard} label="Active subs" value={num(d.subscriptions.active)} accent testid="exec-active-subs" />
        <Metric icon={CreditCard} label="Trial users" value={num(d.subscriptions.trialing)} testid="exec-trialing" />
        <Metric icon={TrendingUp} label="Conversion" value={`${d.subscriptions.conversion_rate}%`} sub="Paying / total users" testid="exec-conversion" />
        <Metric icon={AlertTriangle} label="Churn rate" value={`${d.subscriptions.churn_rate}%`} sub={`${num(d.revenue.failed_payments)} failed payments`} testid="exec-churn" />
      </Section>

      <Section title="Platform activity">
        <Metric icon={Building2} label="Active businesses" value={num(d.platform.active_businesses)} accent testid="exec-businesses" />
        <Metric icon={FileText} label="Documents generated" value={num(d.platform.documents_generated)} testid="exec-documents" />
        <Metric icon={Bot} label="AI conversations" value={num(d.platform.ai_conversations)} testid="exec-ai" />
        <Metric icon={CheckCircle2} label="Workflows completed" value={num(d.platform.workflows_completed)} sub="Formation · Credit · Funding" testid="exec-workflows" />
      </Section>
    </div>
  );
}
